<template>
  <div class="minimal-layout">
    <section class="minimal-header">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="model-info">
              <h1 class="model-title">
                {{ modelData.year }} {{ modelData.make }} {{ modelData.model }}
              </h1>
              <p v-if="selectedTrim" class="model-trim-name">
                {{ selectedTrim.trim || selectedTrim.styleName }}
              </p>
              <p class="model-price">
                {{
                  selectedTrim
                    ? formatCurrency(
                        selectedTrim.baseMsrp || selectedTrim.price || 0,
                      )
                    : `From ${formatCurrency(lowestPrice)}`
                }}
              </p>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="model-image-wrapper">
              <v-img
                :src="currentVehicleImage"
                :alt="`${modelData.year} ${modelData.make} ${modelData.model}`"
                max-height="200"
                contain
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="trim-selector">
      <v-container>
        <v-row>
          <v-col cols="12">
            <h2 class="section-title mb-4">Select Trim</h2>
            <v-select
              :model-value="selectedTrim"
              :items="modelData.trims"
              :item-title="
                (item: TrimData) =>
                  `${item.trim || item.styleName} - ${formatCurrency(item.baseMsrp || item.price || 0)}`
              "
              :item-value="(item: TrimData) => item"
              @update:model-value="$emit('select-trim', $event)"
              label="Choose a trim"
              variant="outlined"
              density="comfortable"
              class="trim-dropdown"
            />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="comparison-grid">
      <v-container>
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="section-title">Quick Compare</h2>
              <v-btn
                v-if="comparisonTrims.length > 0"
                color="error"
                variant="text"
                size="small"
                @click="$emit('clear-comparison')"
              >
                Clear All
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="(trim, index) in modelData.trims"
            :key="trim.id || index"
            cols="12"
            sm="6"
            lg="3"
          >
            <v-card
              class="minimal-trim-card"
              :class="{
                selected: selectedTrim?.id === trim.id,
                'in-comparison': isInComparison(trim.id),
              }"
              variant="outlined"
              @click="$emit('select-trim', trim)"
            >
              <v-card-text>
                <div class="trim-card-header">
                  <h3 class="trim-name">{{ trim.trim || trim.styleName }}</h3>
                  <v-checkbox
                    v-if="showTrimComparing !== false"
                    :model-value="isInComparison(trim.id)"
                    @update:model-value="$emit('add-to-comparison', trim)"
                    @click.stop
                    hide-details
                    density="compact"
                    color="primary"
                  />
                </div>

                <div class="trim-price mb-3">
                  {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
                </div>

                <v-divider class="my-3" />

                <div class="trim-specs">
                  <div class="spec-item">
                    <v-icon size="x-small" class="mr-1">mdi-engine</v-icon>
                    <span>{{ getEngineInfo(trim) }}</span>
                  </div>
                  <div class="spec-item">
                    <v-icon size="x-small" class="mr-1">mdi-gas-station</v-icon>
                    <span
                      >{{ getMpgInfo(trim) }}
                      {{ isElectricVehicle(trim) ? 'MPGe' : 'MPG' }}</span
                    >
                  </div>
                  <div class="spec-item">
                    <v-icon size="x-small" class="mr-1"
                      >mdi-car-shift-pattern</v-icon
                    >
                    <span>{{ getDrivetrain(trim) }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section v-if="availableColors.length > 0" class="color-palette">
      <v-container>
        <h2 class="section-title mb-4">Colors</h2>
        <div class="color-grid">
          <button
            v-for="(color, index) in availableColors"
            :key="index"
            class="color-tile"
            :class="{ active: selectedColor?.hex === color.hex }"
            :style="{ backgroundColor: color.hex || '#ccc' }"
            @click="$emit('select-color', color)"
            :aria-label="`Select ${color.colorName} color`"
          >
            <v-icon
              v-if="selectedColor?.hex === color.hex"
              size="small"
              color="white"
            >
              mdi-check
            </v-icon>
          </button>
        </div>
      </v-container>
    </section>

    <section
      v-if="showTrimComparing !== false && comparisonTrims.length > 1"
      class="detailed-comparison"
    >
      <v-container>
        <h2 class="section-title mb-4">Detailed Comparison</h2>
        <EnhancedTrimComparison
          :trims="comparisonTrims"
          :modelData="modelData"
          @remove-trim="$emit('remove-from-comparison', $event)"
          @clear-comparison="$emit('clear-comparison')"
          @diff-toggle="$emit('diff-toggle', $event)"
        />
      </v-container>
    </section>

    <section v-if="selectedTrim" class="trim-details">
      <v-container>
        <h2 class="section-title mb-4">
          {{ selectedTrim.trim || selectedTrim.styleName }} Specifications
        </h2>

        <v-expansion-panels variant="accordion" class="minimal-panels">
          <v-expansion-panel title="Performance">
            <v-expansion-panel-text>
              <SpecsList
                :specs="getPerformanceSpecs(selectedTrim)"
                icon="mdi-speedometer"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel title="Dimensions">
            <v-expansion-panel-text>
              <SpecsList
                :specs="getDimensionSpecs(selectedTrim)"
                icon="mdi-car"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel title="Safety Features">
            <v-expansion-panel-text>
              <SpecsList
                :specs="getSafetyFeatures(selectedTrim)"
                icon="mdi-shield-check"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel title="Technology">
            <v-expansion-panel-text>
              <SpecsList
                :specs="getTechFeatures(selectedTrim)"
                icon="mdi-chip"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-container>
    </section>

    <section
      v-if="showModelComparing !== false"
      class="model-comparison-section py-10"
    >
      <v-container>
        <div class="section-header mb-8">
          <h2 class="section-title">
            <v-icon start>mdi-car-compare</v-icon>
            Compare With Other Models
          </h2>
          <p class="section-subtitle">
            See how this model stacks up against competitors
          </p>
        </div>

        <slot name="model-comparison"></slot>
      </v-container>
    </section>
  </div>
</template>

<script setup lang="ts">
import EnhancedTrimComparison from '@/components/EnhancedTrimComparison.vue'
import SpecsList from '@/components/SpecsList.vue'
import { formatCurrency, getCDNUrl } from '@/utils'
import {
  getMpgInfo as getMpgInfoUtil,
  parseSpecValue,
  parseSpecItem,
} from '@/utils/modelDetails'
import type {
  TrimData,
  ColorOption,
  ModelData,
  SpecificationCategory,
} from '@/types'

interface SpecItem {
  label: string
  value: string
}

const props = defineProps<{
  modelData: ModelData
  selectedTrim: TrimData | null
  selectedColor: ColorOption | null
  comparisonTrims: TrimData[]
  availableColors: ColorOption[]
  lowestPrice: number
  currentVehicleImage: string
  showTrimComparing?: boolean
  showModelComparing?: boolean
}>()

defineEmits<{
  'select-trim': [trim: TrimData]
  'select-color': [color: ColorOption]
  'add-to-comparison': [trim: TrimData]
  'remove-from-comparison': [trimId: string]
  'clear-comparison': []
  'diff-toggle': [enabled: boolean]
}>()

const isInComparison = (trimId: string): boolean => {
  return props.comparisonTrims.some(t => t.id === trimId)
}

const getSpecValue = (
  trim: TrimData | null | undefined,
  category: string,
  searchTerm: string,
): string => {
  const tech = trim?.technicalSpecification
  const categoryData = tech?.[category] as SpecificationCategory | undefined
  const items = categoryData?.items || []
  const item = items.find((i: string) => i.includes(searchTerm))
  if (item) {
    const value = parseSpecValue(item)
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    return value
  }
  return 'N/A'
}

const getEngineInfo = (trim: TrimData | null | undefined): string => {
  const tech = trim?.technicalSpecification
  const engineData = tech?.Engine as SpecificationCategory | undefined
  const engine = engineData?.items?.[0]
  if (engine) {
    const match = engine.match(/(\d+\.\d+L|\d+L)/i)
    if (match) return match[0]
    const parsed = parseSpecItem(engine, false)
    return (typeof parsed === 'object' ? parsed.label : engine) || 'Engine'
  }
  return 'Engine'
}

const getMpgInfo = (trim: TrimData | null | undefined): string => {
  const mpg = getMpgInfoUtil(trim)
  if (mpg === 'MPG N/A' || mpg === 'N/A') return '--'
  const match = mpg.match(/\d+/)
  return match ? match[0] : '--'
}

const isElectricVehicle = (trim: TrimData | null | undefined): boolean => {
  const cityMPGe = getSpecValue(trim, 'Mileage', 'EPA MPG Equivalent - City')
  const hwMPGe = getSpecValue(trim, 'Mileage', 'EPA MPG Equivalent - Hwy')
  return !!(cityMPGe && cityMPGe !== 'N/A') || !!(hwMPGe && hwMPGe !== 'N/A')
}

const getDrivetrain = (trim: TrimData | null | undefined): string => {
  const drivetrain =
    getSpecValue(trim, 'Transmission', 'Drivetrain') ||
    getSpecValue(trim, 'Transmission', 'Drive')
  if (drivetrain && drivetrain !== 'N/A') {
    return drivetrain
  }
  return 'FWD'
}

const getPerformanceSpecs = (trim: TrimData | null | undefined): SpecItem[] => {
  return [
    { label: 'Engine', value: getSpecValue(trim, 'Engine', 'Engine Type') },
    {
      label: 'Horsepower',
      value: getSpecValue(trim, 'Engine', 'SAE Net Horsepower'),
    },
    { label: 'Torque', value: getSpecValue(trim, 'Engine', 'SAE Net Torque') },
    {
      label: 'Transmission',
      value: getSpecValue(trim, 'Transmission', 'Trans Description'),
    },
    {
      label: 'Drivetrain',
      value: getSpecValue(trim, 'Transmission', 'Drivetrain'),
    },
    { label: 'City MPG', value: getSpecValue(trim, 'Mileage', 'EPA MPG City') },
    {
      label: 'Highway MPG',
      value: getSpecValue(trim, 'Mileage', 'EPA MPG Highway'),
    },
  ].filter(spec => spec.value !== 'N/A')
}

const getDimensionSpecs = (trim: TrimData | null | undefined): SpecItem[] => {
  return [
    {
      label: 'Seating',
      value: getSpecValue(trim, 'Interior Dimensions', 'Passenger Capacity'),
    },
    {
      label: 'Length',
      value: getSpecValue(trim, 'Exterior Dimensions', 'Length'),
    },
    {
      label: 'Width',
      value: getSpecValue(trim, 'Exterior Dimensions', 'Width'),
    },
    {
      label: 'Height',
      value: getSpecValue(trim, 'Exterior Dimensions', 'Height'),
    },
    {
      label: 'Wheelbase',
      value: getSpecValue(trim, 'Exterior Dimensions', 'Wheelbase'),
    },
    {
      label: 'Cargo Volume',
      value: getSpecValue(trim, 'Interior Dimensions', 'Cargo Volume'),
    },
  ].filter(spec => spec.value !== 'N/A')
}

interface FeatureItem {
  label: string
  value: string
}

const getSafetyFeatures = (
  trim: TrimData | null | undefined,
): FeatureItem[] => {
  const safetyItems = trim?.standardEquipment?.SAFETY?.items || []
  return safetyItems.slice(0, 10).map((item: string) => {
    const parsed = parseSpecItem(item, false)
    if (typeof parsed === 'object') {
      return {
        label: parsed.label || item,
        value: parsed.value !== 'N/A' ? parsed.value : '✓',
      }
    }
    return { label: item, value: '✓' }
  })
}

const getTechFeatures = (trim: TrimData | null | undefined): FeatureItem[] => {
  const techItems = trim?.standardEquipment?.TECHNOLOGY?.items || []
  return techItems.slice(0, 10).map((item: string) => {
    const parsed = parseSpecItem(item, false)
    if (typeof parsed === 'object') {
      return {
        label: parsed.label || item,
        value: parsed.value !== 'N/A' ? parsed.value : '✓',
      }
    }
    return { label: item, value: '✓' }
  })
}
</script>

<style scoped>
.minimal-layout {
  width: 100%;
  background: var(--v-theme-background);
}

.minimal-header {
  padding: 40px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.model-title {
  font-size: 2.5rem;
  font-weight: 300;
  letter-spacing: -0.5px;
  margin-bottom: 8px;
  color: var(--v-theme-on-background);
}

.model-trim-name {
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--v-theme-on-background);
  opacity: 0.8;
  margin-bottom: 8px;
}

.model-price {
  font-size: 1.25rem;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.model-image-wrapper {
  display: flex;
  justify-content: flex-end;
}

.trim-selector {
  padding: 32px 0;
  background: var(--v-theme-surface);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--v-theme-on-surface);
  margin: 0;
}

.trim-dropdown {
  max-width: 600px;
}

.comparison-grid {
  padding: 48px 0;
}

.minimal-trim-card {
  height: 100%;
  transition: all 0.2s ease;
  cursor: pointer;
  background: var(--v-theme-surface);
}

.minimal-trim-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.minimal-trim-card.selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.05);
}

.minimal-trim-card.in-comparison {
  border-color: rgb(var(--v-theme-success));
}

.trim-card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.trim-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--v-theme-on-surface);
}

.trim-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.trim-specs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.spec-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface-variant);
}

.color-palette {
  padding: 48px 0;
  background: var(--v-theme-surface);
}

.color-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.color-tile {
  width: 48px;
  height: 48px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  background-clip: padding-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-tile:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.color-tile.active {
  border-color: rgb(var(--v-theme-primary));
  transform: scale(1.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.color-tile:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.detailed-comparison {
  padding: 48px 0;
}

.trim-details {
  padding: 48px 0;
  background: var(--v-theme-surface);
}

.minimal-panels {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.v-theme--dark {
  .minimal-header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .minimal-trim-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }

  .color-tile {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .color-tile:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }

  .minimal-panels {
    border-color: rgba(255, 255, 255, 0.08);
  }
}

@media (max-width: 960px) {
  .model-title {
    font-size: 2rem;
  }

  .model-price {
    font-size: 1.125rem;
  }

  .model-image-wrapper {
    margin-top: 24px;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .model-title {
    font-size: 1.5rem;
  }

  .minimal-header {
    padding: 24px 0;
  }

  .comparison-grid,
  .color-palette,
  .detailed-comparison,
  .trim-details {
    padding: 32px 0;
  }

  .color-tile {
    width: 40px;
    height: 40px;
  }
}

*:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

@media print {
  .minimal-header {
    padding: 20px 0;
  }

  .color-palette {
    display: none;
  }
}

.v-theme--dark {
  .model-comparison-section {
    background: #1e1e1e !important;
  }

  .section-title,
  .section-subtitle {
    color: #e0e0e0;
  }
}
</style>
